import { VisitChecklistEditor } from './visit-checklist-editor.tsx';
import { precedingPhotoBlocker } from '@shared/checklists/photos.ts';
import { PhotoTrigger } from '../photo-viewer.tsx';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { checklistStackApi, fileApi, scheduleApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, TextArea } from '../ui.tsx';
import { useLocalizedContent } from '../../i18n/localized.ts';
import { formatLongDate } from '../../i18n/datetime.ts';
import type { ChecklistItemStatus } from '@shared/checklists/cadence.ts';
import type { VisitChecklistItem, VisitChecklist as VisitChecklistData } from '@shared/types/index.ts';

function clientUpdateId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function initials(name: string | null): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function VisitChecklist({
  appointmentId,
  mode,
  onChanged,
  initialData,
}: {
  appointmentId: string;
  mode: 'panel' | 'sheet';
  onChanged?: () => void;
  initialData?: VisitChecklistData;
}) {
  const { t } = useTranslation();
  const { can, user, company, currentLocation } = useSession();
  const scope = JSON.stringify([
    appointmentId,
    user?.id,
    company?.id,
    currentLocation?.id,
    user?.role,
    user?.permissions,
  ]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const retainedScope = useRef(scope);
  const localized = useLocalizedContent();
  const queryClient = useQueryClient();
  const writable = can('schedule:write') || can('checklists:write');
  const [error, setError] = useState<string | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [retainedPhotos, setRetainedPhotos] = useState<File[]>([]);
  const visibleNotes = retainedScope.current === scope ? noteDrafts : {};
  const visiblePhotos = retainedScope.current === scope ? retainedPhotos : [];
  useEffect(() => {
    retainedScope.current = scope;
    setNoteDrafts({});
    setRetainedPhotos([]);
    setError(null);
  }, [scope]);

  const checklist = useQuery({
    queryKey: ['visit-checklist', appointmentId],
    initialData,
    queryFn: () => scheduleApi.checklist(appointmentId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['visit-checklist', appointmentId] });
    onChanged?.();
  };
  const fail = async (caught: unknown, input: { scope: string }) => {
    if (input.scope !== currentScope.current) return;
    setError(caught instanceof ApiRequestError ? caught.message : t('checklists.saveError'));
    if (caught instanceof ApiRequestError && caught.code === 'CHECKLIST_SNAPSHOT_CHANGED') await refresh();
  };

  const prepare = useMutation({
    mutationFn: () => checklistStackApi.prepare(appointmentId),
    onSuccess: refresh,
    onError: () => setError(t('checklistStack.loadError')),
  });
  const tick = useMutation({
    mutationFn: (input: { itemId: string; status: ChecklistItemStatus; reason?: string; scope: string }) =>
      scheduleApi.tickChecklistItem(appointmentId, input.itemId, {
        clientId: clientUpdateId(),
        checklistRevision: checklist.data?.revision,
        ticked: input.status === 'done',
        tickedAt: new Date().toISOString(),
        ...(input.status === 'skipped' || input.status === 'not_applicable' ? { status: input.status } : {}),
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: async (_, input) => {
      if (input.scope !== currentScope.current) return;
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  const saveNote = useMutation({
    mutationFn: (input: { itemId: string; note: string; scope: string }) =>
      scheduleApi.noteChecklistItem(appointmentId, input.itemId, {
        clientId: clientUpdateId(),
        checklistRevision: checklist.data?.revision,
        note: input.note.trim(),
        notedAt: new Date().toISOString(),
      }),
    onSuccess: async (_, input) => {
      if (input.scope !== currentScope.current) return;
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  const addPhoto = useMutation({
    mutationFn: async (input: { itemId: string; file: File; revision?: number; scope: string }) => {
      const uploaded = await fileApi.uploadVisitPhoto(appointmentId, input.file);
      if (input.scope !== currentScope.current) throw new Error('The photo upload session changed.');
      return scheduleApi.attachChecklistPhoto(appointmentId, input.itemId, {
        clientId: clientUpdateId(),
        checklistRevision: input.revision,
        photoId: uploaded.id,
        attachedAt: new Date().toISOString(),
      });
    },
    onSuccess: async (_, input) => {
      if (input.scope !== currentScope.current) return;
      setError(null);
      await refresh();
    },
    onError: async (caught, input) => {
      if (input.scope !== currentScope.current) return;
      setRetainedPhotos((current) => [...current, input.file]);
      await fail(caught, input);
    },
  });

  if (checklist.isLoading) return null;
  if (!checklist.data) {
    if (checklist.error instanceof ApiRequestError && checklist.error.code === 'CHECKLIST_SNAPSHOT_PENDING')
      return (
        <div className="my-3 space-y-2">
          <Alert tone="info">{t('checklistStack.pending')}</Alert>
          {error && <Alert tone="error">{error}</Alert>}
          <Button busy={prepare.isPending} onClick={() => prepare.mutate()}>
            {t('checklistStack.prepare')}
          </Button>
        </div>
      );
    if (
      checklist.isError &&
      !(checklist.error instanceof ApiRequestError && [401, 403, 404].includes(checklist.error.status))
    )
      return (
        <div className="my-3">
          <Alert tone="error" title={t('checklistStack.loadError')}>
            <Button className="mt-2" busy={checklist.isFetching} onClick={() => void checklist.refetch()}>
              {t('common.retry')}
            </Button>
          </Alert>
        </div>
      );
    return null;
  }
  if (checklist.error instanceof ApiRequestError && [401, 403, 404].includes(checklist.error.status)) return null;
  const data = checklist.data;
  const visibleItems = data.items.filter((item) => item.due || data.readOnly);
  if (data.items.length === 0 && mode === 'sheet') return null;

  return (
    <section
      className={mode === 'panel' ? 'mt-4 border-t border-[color:var(--color-line)] pt-3' : 'mb-4'}
      aria-label={t('checklists.visitHeading')}
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t('checklists.visitHeading')}</h2>
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('checklists.progress', { completed: data.progress.completed, total: data.progress.total })}
        </p>
      </div>
      {data.blockCompletionOnMissingPhotos && (data.missingRequiredPhotos ?? 0) > 0 ? (
        <Alert tone="warning">{t('checklists.missingPhotos', { count: data.missingRequiredPhotos })}</Alert>
      ) : null}
      {error ? (
        <div className="mb-2">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {Object.entries(visibleNotes).some(([id, note]) => note.trim() && !data.items.some((item) => item.id === id)) && (
        <div className="mb-3 space-y-1">
          <p>{t('checklistStack.retainedNotes')}</p>
          {Object.entries(visibleNotes)
            .filter(([id, note]) => note.trim() && !data.items.some((item) => item.id === id))
            .map(([id, note]) => (
              <p key={id} className="whitespace-pre-wrap">
                {note}
              </p>
            ))}
        </div>
      )}
      {visiblePhotos.length > 0 && (
        <div className="mb-3 space-y-1">
          <p>{t('checklistStack.retainedPhotos')}</p>
          {visiblePhotos.map((file, index) => (
            <Button
              key={index}
              onClick={() => {
                const url = URL.createObjectURL(file),
                  link = document.createElement('a');
                link.href = url;
                link.download = file.name;
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              {t('checklistStack.savePhotoCopy', { name: file.name })}
            </Button>
          ))}
        </div>
      )}
      {can('schedule:write') && (user?.role === 'owner' || user?.role === 'manager') && mode === 'panel' && (
        <VisitChecklistEditor key={appointmentId} appointmentId={appointmentId} checklist={data} />
      )}
      {visibleItems.length === 0 ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('checklists.visitEmpty')}</p>
      ) : (
        <ul className="space-y-3">
          {visibleItems.map((item, index) => {
            const blocker = data.enforceOrder ? precedingPhotoBlocker(data.items, item.id) : null;
            return (
              <Fragment key={item.id}>
                {(index === 0 ||
                  (visibleItems[index - 1]?.sectionKey ?? visibleItems[index - 1]?.areaName) !==
                    (item.sectionKey ?? item.areaName)) && (
                  <li className="pt-2">
                    <h3 className="font-semibold">
                      {item.layerName ? `${localized.text(item.layerName, item.layerNameTranslations ?? null)}: ` : ''}
                      {localized.text(item.areaName, item.areaNameTranslations)}
                    </h3>
                  </li>
                )}
                <ChecklistRow
                  item={item}
                  mode={mode}
                  writable={writable && !data.readOnly}
                  photoRequirementsEnforced={data.photoRequirementsEnforced !== false}
                  canOverride={can('checklists:override')}
                  lockedTask={blocker ? localized.text(blocker.taskName, blocker.taskNameTranslations) : null}
                  busy={tick.isPending || saveNote.isPending || addPhoto.isPending}
                  area={localized.text(item.areaName, item.areaNameTranslations)}
                  task={localized.text(item.taskName, item.taskNameTranslations)}
                  description={item.description ? localized.text(item.description, item.descriptionTranslations) : null}
                  noteDraft={visibleNotes[item.id] ?? item.note ?? ''}
                  onNoteDraft={(value) => setNoteDrafts((current) => ({ ...current, [item.id]: value }))}
                  onStatus={(status) => {
                    if (
                      status === 'done' &&
                      data.photoRequirementsEnforced !== false &&
                      item.requiresPhoto &&
                      item.photos.length === 0
                    )
                      return;
                    tick.mutate({
                      scope,
                      itemId: item.id,
                      status,
                      ...(status === 'not_applicable' && item.requiresPhoto
                        ? { reason: visibleNotes[item.id] ?? item.note ?? '' }
                        : {}),
                    });
                  }}
                  onSaveNote={() =>
                    saveNote.mutate({ scope, itemId: item.id, note: visibleNotes[item.id] ?? item.note ?? '' })
                  }
                  onPhoto={(file) => addPhoto.mutate({ scope, itemId: item.id, file, revision: data.revision })}
                />
              </Fragment>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ChecklistRow({
  item,
  mode,
  writable,
  canOverride,
  photoRequirementsEnforced,
  lockedTask,
  busy,
  area,
  task,
  description,
  noteDraft,
  onNoteDraft,
  onStatus,
  onSaveNote,
  onPhoto,
}: {
  item: VisitChecklistItem;
  mode: 'panel' | 'sheet';
  writable: boolean;
  canOverride: boolean;
  photoRequirementsEnforced: boolean;
  lockedTask: string | null;
  busy: boolean;
  area: string;
  task: string;
  description: string | null;
  noteDraft: string;
  onNoteDraft: (value: string) => void;
  onStatus: (status: ChecklistItemStatus) => void;
  onSaveNote: () => void;
  onPhoto: (file: File) => void;
}) {
  const { t } = useTranslation();
  const done = item.status === 'done';
  const mark = initials(item.completedByName);
  const missingRequiredPhoto = photoRequirementsEnforced && item.requiresPhoto && item.photos.length === 0;
  const cannotMarkDone = (missingRequiredPhoto && !done) || lockedTask !== null;

  return (
    <li className={item.due ? '' : 'opacity-70'}>
      <div className="flex items-start gap-2">
        {mode === 'sheet' ? (
          <button
            type="button"
            className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-[color:var(--color-line)] text-sm"
            aria-pressed={done}
            aria-label={`${area} · ${task}`}
            disabled={!writable || busy || cannotMarkDone}
            title={cannotMarkDone ? t('checklists.photoRequiredBeforeDone') : undefined}
            onClick={() => {
              if (!done && missingRequiredPhoto) return;
              onStatus(done ? 'pending' : 'done');
            }}
          >
            {done ? '✓' : ''}
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <span className="text-[color:var(--color-muted)]">{area}</span>
            {' · '}
            <span className={done ? 'line-through' : 'font-medium'}>{task}</span>
            {item.frequency !== 'every_visit' ? (
              <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                {t(`servicesStep.frequency.${item.frequency}`)}
              </span>
            ) : null}
            {!item.due ? (
              <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                {t('checklists.notDue')}
              </span>
            ) : null}
            {item.requiresPhoto ? (
              <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                {t('checklists.photoRequired')}
              </span>
            ) : null}
          </p>
          {lockedTask ? <p className="text-sm">{t('checklists.orderLocked', { task: lockedTask })}</p> : null}
          {description ? <p className="text-sm text-[color:var(--color-muted)]">{description}</p> : null}
          {done && mark ? (
            <p className="text-sm text-[color:var(--color-muted)]">
              {t('checklists.doneBy', {
                initials: mark,
                when: item.completedAt ? formatLongDate(item.completedAt.slice(0, 10)) : '',
              })}
            </p>
          ) : null}
          {item.status === 'skipped' ? <p className="text-sm">{t('checklists.status.skipped')}</p> : null}
          {item.status === 'not_applicable' ? <p className="text-sm">{t('checklists.status.not_applicable')}</p> : null}
          {item.note ? <p className="text-sm">{item.note}</p> : null}
          {missingRequiredPhoto && !done ? (
            <p className="text-sm text-[color:var(--color-muted)]">{t('checklists.photoRequiredBeforeDone')}</p>
          ) : null}
          {item.photos.length > 0 ? (
            <ul className="mt-1 flex flex-wrap gap-2">
              {item.photos.map((photo) => (
                <li key={photo.id}>
                  <PhotoTrigger src={photo.downloadUrl} alt={`${area} · ${task}`} group={`checklist-${item.id}`}>
                    <img src={photo.downloadUrl} alt={`${area} · ${task}`} className="h-16 w-16 rounded object-cover" />
                  </PhotoTrigger>
                </li>
              ))}
            </ul>
          ) : null}
          {mode === 'panel' && writable ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {item.status !== 'done' ? (
                <Button disabled={busy || missingRequiredPhoto || lockedTask !== null} onClick={() => onStatus('done')}>
                  {t('checklists.markDone')}
                </Button>
              ) : (
                <Button variant="secondary" disabled={busy} onClick={() => onStatus('pending')}>
                  {t('checklists.markPending')}
                </Button>
              )}
              <Button variant="ghost" disabled={busy || item.requiresPhoto} onClick={() => onStatus('skipped')}>
                {t('checklists.markSkipped')}
              </Button>
              <Button
                variant="ghost"
                disabled={busy || (item.requiresPhoto && (!canOverride || !noteDraft.trim()))}
                onClick={() => onStatus('not_applicable')}
              >
                {t('checklists.markNa')}
              </Button>
            </div>
          ) : null}
          {mode === 'panel' && writable ? (
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <TextArea
                label={t('checklists.noteLabel')}
                rows={2}
                value={noteDraft}
                onChange={(event) => onNoteDraft(event.target.value)}
              />
              <Button variant="secondary" disabled={busy} onClick={onSaveNote}>
                {t('checklists.saveNote')}
              </Button>
              <label className="btn btn-secondary cursor-pointer">
                {t('checklists.addPhoto')}
                <input
                  type="file"
                  accept="image/*"
                  capture={item.requiresPhoto ? 'environment' : undefined}
                  disabled={!writable || busy || lockedTask !== null}
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) onPhoto(file);
                  }}
                />
              </label>
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}
